<template>
  <v-card class="d-flex flex-column pa-3 pt-0" light height="420px">
    <v-card-text class="pb-0 px-6">
      <v-list>
        <v-row justify="center">
          <v-col class="px-1">
            <p class="pb-12 px-0">
              {{
                $t("Choose a start and end time for the selected recording")
              }}:
            </p>
            <vue-slider
              class="pb-8 px-5"
              :contained="true"
              v-model="sliderValue"
              :marks="marks"
              :drag-on-click="false"
              :enable-cross="false"
              :max-range="maxRange"
              :min-range="minRange"
              :tooltip="'always'"
              :min="min"
              :max="max"
              :tooltip-placement="['top', 'bottom']"
              :process-style="{ backgroundColor: primaryColor }"
              :clickable="false"
              @drag-end="forceRange"
            >
              <template v-slot:tooltip="{ value, focus }">
                <div :class="['custom-tooltip', { focus }]">
                  {{ formatDate(addMiliseconds(value), true, showSeconds) }}
                </div>
              </template>
              <template v-slot:label="{ label, active }">
                <div
                  :class="['vue-slider-mark-label', 'custom-label', { active }]"
                >
                  {{ label }}
                </div>
              </template>
            </vue-slider>
            <p class="mt-12">
              {{
                $t(
                  "If you wish to export a video from another time, move your timeline to a different recording."
                )
              }}
            </p>
            <v-checkbox
              class="black-label-text"
              v-model="printDate"
              hide-details
              :label="$t('Include date and time in exported video')"
            ></v-checkbox>
          </v-col>
        </v-row>
      </v-list>
    </v-card-text>
    <v-spacer></v-spacer>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn depressed @click="exportVideo" :disabled="exportDisabled" color="primary">{{
        $t("Export")
      }}</v-btn>
      <v-btn depressed @click="closeOverlay">{{ $t("Close") }}</v-btn>
    </v-card-actions>
  </v-card>
</template>
<script>
import { reactive, toRefs, computed, watch, onMounted } from "../../plugins/compositionApi";
import Theme from "../../service/themes";
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/default.css";
import restapi from "../../service/CMApi";
import { saveFile } from "../../service/main";
import { getConfiguration } from "../../service/main";
import { store } from "@/store/index";
import { t } from "../../service/locale";
import { getMessageFromError } from "../../service/errors";

export default {
  name: "VideoExportDialog",
  components: {
    VueSlider
  },
  props: {
    camera: {
      type: Object
    },
    currentRecording: Object,
    currentTime: Number,
    showDialog: Boolean,
    offset: Number,
    rights: Object,
    accountTimeOffset: Number
  },
  setup(props, context) {
    const config = getConfiguration();
    const themeName = config.hasOwnProperty("theme")
      ? config.theme
      : "Cameramanager";
    const colors = new Theme(themeName).colors();
    const { formatDate, addMiliseconds, removeMiliseconds } = timeHelpers(props);

    const data = reactive({
      downloadingVideo: false,
      exportReady: false,
      checkingJob: false,
      jobId: null,
      printDate: true,
      exportDisabled: false,
      primaryColor: colors.themes.light.primary,
      cameraId: computed(() => (props.camera ? props.camera.cameraId : null)),
      cameraName: computed(() => (props.camera ? props.camera.name : "camera")),
      recordingId: computed(() =>
        props.currentRecording ? props.currentRecording.recordingId : null
      ),
      sliderValue: [0, 50],
      startTime: null,
      endTime: null,
      maxRange: 1800,
      minRange: 15,
      showSeconds: false,
      min: computed(() =>
        data.startTime
          ? data.startTime
          : removeMiliseconds(new Date().getTime() - 3600000)
      ),
      max: computed(() =>
        data.endTime ? data.endTime : removeMiliseconds(new Date().getTime())
      ),
      marks: computed(() => {
        let marks = {};
        marks[data.min] = {
          label: formatDate(addMiliseconds(data.min), true, data.showSeconds),
          style: {
            width: "8px",
            height: "8px",
            display: "block",
            backgroundColor: "grey",
            transform: "translate(-2px, -2px)"
          }
        };

        let date = new Date(addMiliseconds(data.min));
        date.setHours(date.getHours() + 1);
        date.setMinutes(0);
        date.setSeconds(0);

        let dt = new Date(date).getTime();

        let text = null;
        let dt2 = null;
        let prev = formatDate(dt, false, data.showSeconds);
        let hrs = null;
        let min = null;

        for (let i = removeMiliseconds(dt); i < data.max; i += 3600) {
          text = formatDate(addMiliseconds(i), false, data.showSeconds);
          if (prev !== text) {
            marks[i] = {
              label: text,
              style: {
                width: "8px",
                height: "8px",
                display: "block",
                backgroundColor: "grey",
                transform: "translate(-2px, -2px)"
              }
            };
            prev = text;
          } else {
            marks[i] = "";
          }
        }

        marks[data.max] = {
          label: formatDate(addMiliseconds(data.max), true, data.showSeconds),
          style: {
            width: "8px",
            height: "8px",
            display: "block",
            backgroundColor: "grey",
            transform: "translate(-2px, -2px)"
          }
        };
        return marks;
      }),
      checkJobInterval: null
    });

    watch(() => data.printDate, () => {
      forceRange && forceRange();
    });

    onMounted(() => {
      data.exportDisabled = false;
      let start = new Date(props.currentRecording.startTime);
      let end = props.currentRecording.closed
        ? new Date(props.currentRecording.endTime)
        : new Date();
      start.setSeconds(start.getSeconds() + 1);
      data.startTime = removeMiliseconds(start.getTime());
      data.endTime = removeMiliseconds(end.getTime());
      let length = data.endTime - data.startTime;
      if (length < 3600) {
        data.showSeconds = true;
      } else {
        data.showSeconds = false;
      }
      let currentTime = removeMiliseconds(
        new Date(props.currentTime).getTime()
      );
      if (length > 21600) {
        data.startTime = Math.max(currentTime - 10800, data.startTime);
        data.endTime = Math.min(currentTime + 10800, data.endTime);
      }
      data.sliderValue = [
        Math.max(currentTime - 600, data.startTime),
        Math.min(currentTime + 600, data.endTime)
      ];
    });

    const { exportVideo, closeOverlay, forceRange } = videoExportOverlay(
      data,
      context,
      props
    );

    return {
      ...toRefs(data),
      exportVideo,
      closeOverlay,
      formatDate,
      addMiliseconds,
      forceRange
    };
  }
};

function videoExportOverlay(data, context, props) {
  const { formatDate, addMiliseconds, removeMiliseconds } = timeHelpers();

  async function exportVideo() {
    if (props.rights && props.rights.demoUser) {
      return store.dispatch("demoMessage")
    }
    data.exportDisabled = true;
    setTimeout(function(){ data.exportDisabled = false; }, 2000);
    data.exportReady = false;
    let body = {
      minTimestamp: new Date(addMiliseconds(data.sliderValue[0])).toISOString(),
      maxTimestamp: new Date(addMiliseconds(data.sliderValue[1])).toISOString(),
      timestampOptions: {
        enabled: data.printDate
      }
    };
    try {
      if (data.printDate) {
        let result = await restapi.generateExportedVideo(
          data.cameraId,
          data.recordingId,
          body
        );
        data.jobId = result.jobId;
        context.emit("export");
      } else {
        try {
          const recordingUrl = await restapi.getMp4Url(data.cameraId, data.recordingId)
          const from = encodeURIComponent(body.minTimestamp.slice(0, -1)+"+0000")
          const till = encodeURIComponent(body.maxTimestamp.slice(0, -1)+"+0000")
          const downloadUrl = `${recordingUrl.data.urls.mp4Https}?access_token=${recordingUrl.token}&from=${from}&till=${till}`
          const exportJob = {
            id: data.recordingId + new Date().toISOString(),
            status: 'Done',
            type: 'Video Export (no timestamp)',
            size: 'n/a',
            time: new Date(new Date().getTime() - (props.accountTimeOffset - new Date().getTimezoneOffset()) * 60000).toString().substr(0, 24),
            url: downloadUrl,
            fakeJob: true
          }
          store.dispatch('newExport', exportJob)
          context.emit('export')
        } catch (error) {
            store.dispatch('toastMessage', {
              showing: true,
              text: getMessageFromError(error, "REQUEST_MP4_URL"),
              timeout: -1,
              color: 'error',
              support: true
            })
          }
      }
    } catch (error) {
      store.dispatch("toastMessage", {
        showing: true,
        text: getMessageFromError(error, "GENERATE_EXPORTED_VIDEO"),
        timeout: -1,
        color: 'error',
        support: true
      })
    }
  }

  function closeOverlay() {
    context.emit("close");
  }

  function forceRange() {
    if (data.printDate) {
      data.maxRange = 1800;
      if (data.sliderValue[1] - data.sliderValue[0] > 1850) {
        data.sliderValue = [data.sliderValue[0], data.sliderValue[0] + 1800];
      }
    } else {
      //requesting more than 12 hours of recording might cause errors from the API
      //requesting more than 3-4 hours might cause problems when downloading via browser
      data.maxRange = 14400;
    }
  }

  return { exportVideo, closeOverlay, forceRange };
}

function timeHelpers(props) {
  function formatDate(time, hasTime = true, showSeconds = false) {
    let date = new Date(time - props.offset);
    let day = ("0" + date.getDate()).slice(-2);
    let month = ("0" + (date.getMonth() + 1)).slice(-2);
    if (hasTime) {
      let hour = ("0" + date.getHours()).slice(-2);
      let minute = ("0" + date.getMinutes()).slice(-2);
      let seconds = ("0" + date.getSeconds()).slice(-2);
      if (showSeconds) {
        return `${day}/${month} ${hour}:${minute}:${seconds}`;
      } else {
        return `${day}/${month} ${hour}:${minute}`;
      }
    } else {
      return `${day}/${month}`;
    }
  }

  function removeMiliseconds(time) {
    return parseInt(time.toString().slice(0, -3));
  }

  function addMiliseconds(time) {
    return parseInt(time.toString() + "000");
  }

  return { formatDate, addMiliseconds, removeMiliseconds };
}
</script>

<style lang="scss" scoped>
@import "../../assets/styles/main";

.custom-tooltip {
  background-color: $primary;
  color: white;
  border-radius: 3px;
  padding: 1px 4px 0 4px;
  text-align: center;
  // white-space: pre;
}
.custom-label {
  width: 60px;
  white-space: pre-wrap;
  text-align: center;
}
.custom-label.active {
  color: #3498db;
}
.black-label-text {
  &::v-deep label {
    color: #555555;
    font-size: 14px;
  }
}
.overlay-z {
  z-index: 1000 !important;
}
</style>
